import Constants from 'expo-constants';
import * as Linking from 'expo-linking';

import { Logger } from './logging';

/**
 * Thrown when no email app is available to handle the mailto: link
 * (e.g. the user has no mail app installed). Lets callers distinguish this
 * from generic failures and show a clearer message.
 */
export class NoEmailAppError extends Error {
  constructor(message = 'No email client available') {
    super(message);
    this.name = 'NoEmailAppError';
  }
}

export interface OpenSupportEmailParams {
  userId?: string;
  customerId?: string;
  recipientEmail?: string;
  subject?: string;
  bodyPrefix?: string;
  /** Replaces the default metadata body entirely when provided. */
  body?: string;
}

/**
 * Opens the default email client with a pre-filled support email.
 * Includes application metadata and user information in the message body.
 *
 * Callers can override the recipient, subject, and body to reuse the same
 * mailto mechanism for other inboxes (e.g. app-review feedback).
 *
 * @param params - Optional user information and overrides to include
 * @param params.userId - The user's ID
 * @param params.customerId - The customer's ID
 * @param params.recipientEmail - Override for the recipient email address
 * @param params.subject - Override for the email subject
 * @param params.bodyPrefix - Prepended before the metadata block
 * @param params.body - Full body override (replaces metadata entirely)
 *
 * @throws {Error} If unable to open email client or construct mailto URL
 *
 * @example
 * ```tsx
 * try {
 *   await openSupportEmail({
 *     userId: '123',
 *     customerId: '456'
 *   });
 * } catch (error) {
 *   // Handle error (e.g., show toast notification)
 * }
 * ```
 */
export async function openSupportEmail(params?: OpenSupportEmailParams) {
  try {
    const appName = Constants.expoConfig?.name || 'SQUIRE';
    const appVersion =
      Constants.expoConfig?.extra?.EXPO_APP_VERSION ||
      Constants.expoConfig?.version ||
      '';

    const separator = '________________________________';
    const metaLines = [
      '\n\n\n\n\n\n\n\n\n\n',
      separator,
      `Application name: ${appName}`,
      `Application version: ${appVersion}`,
      params?.userId ? `User Id: ${params.userId}` : undefined,
      params?.customerId ? `Customer Id: ${params.customerId}` : undefined,
    ].filter((line) => line !== undefined);

    const messageBody =
      params?.body ??
      (params?.bodyPrefix
        ? `${params.bodyPrefix}\n${metaLines.join('\n')}`
        : metaLines.join('\n'));

    const recipientEmail = params?.recipientEmail ?? 'support@getsquire.com';
    const subject = params?.subject ?? 'SQUIRE - Support Request';

    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(messageBody);
    const mailtoUrl = `mailto:${recipientEmail}?subject=${encodedSubject}&body=${encodedBody}`;

    const canOpen = await Linking.canOpenURL(mailtoUrl);

    if (!canOpen) {
      Logger.error('Cannot open email client', { mailtoUrl });
      throw new NoEmailAppError();
    }

    await Linking.openURL(mailtoUrl);
  } catch (error) {
    Logger.error('Failed to open support email', { error });
    throw error;
  }
}
